import React, { useState, useEffect } from 'react';
import XLSX from 'xlsx';
import { isEmpty, cloneDeep } from 'lodash';
import ShipmentModule from 'components/modules/shipments/ShipmentDetailsContainer';
import Container from 'components/v2/container';
import CargomaticModal from 'components/v2/modal';
import CargomaticDropzone from 'components/v2/DropZone';
import CargomaticInput from 'components/v2/input';
import { showToast } from 'stores/ToastStore';
import ShipmentUtils from 'utils/ShipmentWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import Overlay from 'components/v2/Overlay';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import formatMoney from 'utils/formatters/Money';
import { getCurrencySymbol } from 'components/modules/master/formatters';
import FileDownload from 'react-file-download';
import { PAYABLES, RECEIVABLES } from '../../../constants/LedgerTypes';
import DownloadIcon from '../../v2/icons/DownloadIcon';
const BASE_COST = 'base_cost';
const SHIPPER_COST = 'shipper_cost';
const CARRIER_COST = 'carrier_cost';
const INTERNAL_ADJUSTMENT = 'internal_adjustment';
const RATE_ADJUSTMENT = 'rate_adjustments';
const gridColumnStyle = { gridTemplateColumns: '.25fr 1.25fr 1.25fr .75fr' };
const csvTemplate = 'reference_number,shipper_adjustment,carrier_adjustment,reason \n' +
    'TK22131212                                                    \n' +
    '87472217                                                      \n' +
    '3213123123                                                    \n' +
    '1323213213                                                    \n';
const convertArrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
};
const InfoPanel = ({ shipmentData }) => {
    const { t } = useTranslation();
    const hasDuplicates = () => {
        const data = Object.keys(shipmentData).reduce((total, reference) => {
            if (shipmentData[reference] &&
                shipmentData[reference].shipments.length > 1) {
                total.push(true);
            }
            total.push(false);
            return total;
        }, []);
        return data.some((duplicate) => duplicate);
    };
    if (!hasDuplicates()) {
        return null;
    }
    const getShipmentsWithDuplicates = () => {
        return Object.keys(shipmentData).reduce((list, reference) => {
            const { shipments } = shipmentData[reference];
            if (shipments.length > 1) {
                list.push(React.createElement("p", { key: reference, className: "pl-2 font-regular text-red" }, reference));
            }
            return list;
        }, []);
    };
    return (React.createElement("div", { style: { background: '#FBE3E4' }, className: "flex flex-col mb-8 pb-8" },
        React.createElement("h4", { className: "pl-2 font-medium text-red" }, t('SHIPMENT_FOUND_WITH_REFERENCE_NUMBERS')),
        getShipmentsWithDuplicates()));
};
const ShipmentDetailsModal = ({ show, shipmentId, onHide }) => {
    const { t } = useTranslation();
    return (React.createElement(CargomaticModal, { show: show, onHide: onHide, title: titleize(t('DETAIL', { count: 1 })), body: React.createElement(ShipmentModule, { modalID: shipmentId, dontRefresh: true }), footer: null, location: "top", dialogClassName: "modal-override" }));
};
const Row = ({ shipmentId, type, adjustments, setAdjustmentData, rowData, currency, shipment_type, }) => {
    const getAccessorialCosts = (ledger) => {
        const accessorials = ledger.filter((adj) => adj.category === 'accessorials');
        if (!accessorials.length) {
            return 0;
        }
        return accessorials.reduce((total, current) => {
            total += current.value;
            return total;
        }, 0);
    };
    const getIntermodalShipmentTypeBaseCost = (ledger, type) => {
        return ledger
            .filter((adj) => {
            return (adj.type === type &&
                adj.adjusted_item === BASE_COST &&
                adj.category === BASE_COST);
        })
            .reduce((total, current) => {
            total += parseFloat(current.value || 0);
            return total;
        }, 0);
    };
    const getNonIntermodalShipmentTypeBaseCost = (ledger, adjusted_item) => {
        const baseCost = ledger.find((adj) => adj.category === BASE_COST);
        const baseCostAdjustments = ledger.filter((adj) => adj.type === INTERNAL_ADJUSTMENT &&
            (adj.adjusted_item === adjusted_item ||
                adj.adjusted_item === BASE_COST));
        if (!baseCost && !(baseCostAdjustments && baseCostAdjustments.length)) {
            return 0;
        }
        return baseCostAdjustments.reduce((total, current) => {
            total += parseFloat(current.value);
            return total;
        }, (baseCost && baseCost.value) || 0);
    };
    const getNewTotal = (current, adjustment) => {
        return parseFloat(current) + parseFloat(adjustment);
    };
    const formatData = (data) => {
        return Number.isNaN(data)
            ? formatMoney(0.0, currency)
            : formatMoney(data, currency);
    };
    const ledger_type = type === 'shipper' ? RECEIVABLES : PAYABLES;
    const getBaseCost = isIntermodalShipmentType(shipment_type)
        ? getIntermodalShipmentTypeBaseCost
        : getNonIntermodalShipmentTypeBaseCost;
    const adjusted_item = ledger_type === RECEIVABLES ? SHIPPER_COST : CARRIER_COST;
    const baseCost = getBaseCost(adjustments, adjusted_item);
    const adjustmentValue = rowData >= 0 ? rowData : '';
    const onChange = (value) => setAdjustmentData((currentState) => {
        return Object.assign(Object.assign({}, currentState), { [shipmentId]: Object.assign(Object.assign({}, currentState[shipmentId]), { [type]: value }) });
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("p", { className: "pl-2 pt-2" }, formatData(baseCost)),
        React.createElement("p", { className: "pt-2" }, formatData(getAccessorialCosts(adjustments))),
        React.createElement("div", { className: "flex", style: { width: '80px' } },
            React.createElement("p", { className: "pt-2 pr-2" }, getCurrencySymbol(currency)),
            React.createElement(CargomaticInput, { id: shipmentId, onChange: onChange, value: adjustmentValue, type: "text", hideError: true, hideLabel: true })),
        React.createElement("p", { className: "pt-2" }, formatData(getNewTotal(adjustmentValue || 0, getAccessorialCosts(adjustments))))));
};
const GridRow = ({ shipment, adjustmentData, setAdjustmentData, applyToAll, setShipment, removeShipment, }) => {
    const { t } = useTranslation();
    const [errors, setError] = useState({});
    const shipperData = adjustmentData.shipper;
    const carrierData = adjustmentData.carrier;
    const reason = adjustmentData.reason || '';
    const reasonOnChange = (value) => setAdjustmentData((currentState) => {
        return Object.assign(Object.assign({}, currentState), { [shipment._id]: Object.assign(Object.assign({}, currentState[shipment._id]), { reason: value }) });
    });
    const shipmentDetailsOnClick = () => {
        setShipment(shipment._id);
    };
    useEffect(() => {
        if ((shipperData || carrierData) && !reason) {
            setError((currentState) => {
                return Object.assign(Object.assign({}, currentState), { reason: {
                        message: t('REASON_REQUIRED'),
                    } });
            });
        }
        else {
            setError((currentState) => {
                return Object.assign(Object.assign({}, currentState), { reason: null });
            });
        }
    }, [adjustmentData]);
    return (React.createElement("div", { key: shipment._id, style: gridColumnStyle, className: "grid gap-2 h-16" },
        React.createElement("a", { className: "pt-2", onClick: shipmentDetailsOnClick }, shipment.shipment_id),
        React.createElement("div", { style: {
                background: '#a8f0db',
            }, className: "grid grid-cols-4 gap-2" },
            React.createElement(Row, { shipmentId: shipment._id, type: "shipper", adjustments: shipment.receivables, setAdjustmentData: setAdjustmentData, rowData: shipperData, currency: shipment === null || shipment === void 0 ? void 0 : shipment.currency, shipment_type: shipment.shipment_type })),
        React.createElement("div", { style: {
                background: 'lightgray',
            }, className: "grid grid-cols-4 gap-2" },
            React.createElement(Row, { shipmentId: shipment._id, type: "carrier", adjustments: shipment.payables, setAdjustmentData: setAdjustmentData, rowData: carrierData, currency: shipment === null || shipment === void 0 ? void 0 : shipment.currency, shipment_type: shipment.shipment_type })),
        React.createElement("div", { style: { gridTemplateColumns: '1fr .5fr .25fr' }, className: "grid gap-2" },
            React.createElement(CargomaticInput, { hideLabel: true, type: "text", placeholder: t('ENTER_A_REASON'), value: reason, onChange: reasonOnChange, error: errors.reason }),
            React.createElement("button", { style: { width: '70px', lineHeight: '16px' }, className: "h-8 text-center button button-green", onClick: () => applyToAll(shipment._id) }, titleize(t('APPLY'))),
            React.createElement("i", { onClick: () => removeShipment(shipment._id), className: "icon glyphicon glyphicon-remove ml-2 mt-2" }))));
};
const List = ({ shipmentData, adjustmentData, setAdjustmentData, applyToAll, setShipment, removeShipment, }) => {
    const { t } = useTranslation();
    return (React.createElement("div", null,
        React.createElement("div", { style: gridColumnStyle, className: "grid gap-2 font-medium h-8" },
            React.createElement("p", null, t('SHIPMENT', { count: 1 })),
            React.createElement("div", { style: {
                    background: '#a8f0db',
                }, className: "grid gap-2 grid-cols-4" },
                React.createElement("p", { className: "pl-2" }, t('BASE_COST')),
                React.createElement("p", null, t('ACCESSORIAL_COST')),
                React.createElement("p", null, t('ADJUST_BASE_COST')),
                React.createElement("p", null, t('RESULTING_TOTAL'))),
            React.createElement("div", { style: {
                    background: 'lightgray',
                }, className: "grid gap-2 grid-cols-4" },
                React.createElement("p", { className: "pl-2" }, t('BASE_COST')),
                React.createElement("p", null, t('ACCESSORIAL_COST')),
                React.createElement("p", null, t('ADJUST_BASE_COST')),
                React.createElement("p", null, t('RESULTING_TOTAL'))),
            React.createElement("div", { style: { gridTemplateColumns: '1fr .5fr .25fr' }, className: "grid gap-2" },
                React.createElement("p", null, t('REASON')),
                React.createElement("p", null, t('APPLY_TO_ALL')),
                React.createElement("p", null, t('ACTIONS')))),
        React.createElement("div", { className: "" }, Object.keys(shipmentData).reduce((rows, reference) => {
            const data = shipmentData[reference];
            data.shipments.forEach((shipment, index) => {
                rows.push(React.createElement(GridRow, { key: `${shipment._id}-${index}`, shipment: shipment, adjustmentData: adjustmentData[shipment._id] || {}, setAdjustmentData: setAdjustmentData, applyToAll: applyToAll, setShipment: setShipment, removeShipment: removeShipment(reference) }));
            });
            return rows;
        }, []))));
};
const findBaseCostAdjustmentEquipment = (ledgerItems) => {
    var _a;
    return (_a = ledgerItems.find((el) => el.category === BASE_COST &&
        el.adjusted_item === BASE_COST &&
        el.type === SHIPPER_COST)) === null || _a === void 0 ? void 0 : _a.equipment;
};
const baseCostExists = (ledger, shipment_type, ledgerItems = []) => {
    const type = ledger === RECEIVABLES ? SHIPPER_COST : CARRIER_COST;
    if (isIntermodalShipmentType(shipment_type)) {
        return !!ledgerItems.find((el) => el.category === BASE_COST &&
            el.adjusted_item === BASE_COST &&
            el.type === type);
    }
    return !!ledgerItems.find((el) => (el.category === BASE_COST || el.category === RATE_ADJUSTMENT) &&
        (el.adjusted_item === BASE_COST || el.adjusted_item === type) &&
        (el.type === INTERNAL_ADJUSTMENT || el.type === type));
};
const UploadModal = ({ showModal, setShipmentData, setAdjustmentData, setModalVisibility, setUploading, onHide, }) => {
    const { t } = useTranslation();
    const Dropzone = () => {
        const dropzoneStyle = {
            display: 'flex',
            height: '50px',
            width: 'auto',
            border: '2px dashed black',
            borderRadius: '4px',
        };
        const onFileDrop = async (file) => {
            if (!file || (file && !file.length)) {
                showToast(t('BULK_BASE_COST_ADJUSTMENTS'), t('CSV_FILES_ALLOWED'), 'error');
            }
            else {
                const wb = XLSX.read(convertArrayBufferToBase64(await file[0].arrayBuffer()), { type: 'base64' });
                const sheetName = wb.SheetNames[0];
                const adjustments = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], {
                    header: 1,
                    raw: true,
                });
                adjustments.shift(); // Remove header element
                setModalVisibility(false);
                setUploading(true);
                const referenceNumbers = adjustments.reduce((data, row) => {
                    if (row && row.length) {
                        data.push(row[0]);
                    }
                    return data;
                }, []);
                const shipments = await ShipmentUtils.getShipmentsByShipmentID(referenceNumbers);
                if (shipments && shipments.items && shipments.items.length) {
                    const shipmentAdded = {};
                    const adjustmentData = {};
                    const shipmentData = adjustments.reduce((data, current) => {
                        var reference;
                        if (current && current.length)
                            reference = current[0].toString().trim().toUpperCase();
                        else
                            reference = '';
                        if (!data[reference]) {
                            data[reference] = {
                                shipments: [],
                            };
                        }
                        shipments.items.forEach((shipment) => {
                            const hasMatchingRefs = shipment.reference_numbers.some((ref) => {
                                const optimizedRef = ref && ref.optimized ? ref.optimized : '';
                                if (optimizedRef) {
                                    return (optimizedRef.toString().trim().toUpperCase() === reference);
                                }
                                return '';
                            });
                            if (hasMatchingRefs) {
                                if (!shipmentAdded[shipment._id]) {
                                    data[reference].shipments.push(shipment);
                                    adjustmentData[shipment._id] = {
                                        shipper: current && current.length > 1 && current[1] >= 0
                                            ? parseFloat(current[1])
                                            : '',
                                        carrier: current && current.length > 2 && current[2] >= 0
                                            ? parseFloat(current[2])
                                            : '',
                                        reason: (current && current.length > 3 && current[3]) || '',
                                        reference,
                                        shipment_type: shipment.shipment_type,
                                        equipment: findBaseCostAdjustmentEquipment(shipment.receivables),
                                        shipperBaseCostExists: baseCostExists(RECEIVABLES, shipment.shipment_type, shipment.receivables),
                                        carrierBaseCostExists: baseCostExists(PAYABLES, shipment.shipment_type, shipment.payables),
                                    };
                                    shipmentAdded[shipment._id] = 1;
                                }
                            }
                        });
                        return data;
                    }, {});
                    setShipmentData(shipmentData);
                    setAdjustmentData(adjustmentData);
                    setUploading(false);
                }
                else {
                    setShipmentData({});
                    setAdjustmentData({});
                    setUploading(false);
                }
            }
        };
        return (React.createElement("div", null,
            React.createElement("p", { className: "pb-2" }, t('UPLOAD_CSV_FILE')),
            React.createElement(CargomaticDropzone, { onDrop: onFileDrop, multiple: false, style: dropzoneStyle, dropzoneMessage: t('DROPZONE_MSG'), acceptedFiles: ".csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values" })));
    };
    return (React.createElement(CargomaticModal, { showCloseButton: true, title: t('SHIPMENT_REFERENCE_NUMBERS'), location: "middle", show: showModal, body: Dropzone(), onHide: onHide, footer: null }));
};
const BulkAdjustments = () => {
    const { t } = useTranslation();
    const [showModal, setModalVisibility] = useState(false);
    const [currentShipment, setShipment] = useState('');
    const [shipmentData, setShipmentData] = useState({});
    const [adjustmentData, setAdjustmentData] = useState({});
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    useEffect(() => { }, [shipmentData, adjustmentData, loading]);
    const resetData = () => {
        setAdjustmentData({});
        setShipmentData({});
    };
    function onHideModal() {
        setModalVisibility(false);
    }
    const applyToAll = (shipment) => {
        const dataToCopy = adjustmentData[shipment];
        const dataToApply = Object.keys(adjustmentData).reduce((data, current) => {
            const currentAdjustmentData = adjustmentData[current];
            data[current] = Object.assign(Object.assign({}, currentAdjustmentData), { shipper: currentAdjustmentData.shipper || dataToCopy.shipper, carrier: currentAdjustmentData.carrier || dataToCopy.carrier, reason: currentAdjustmentData.reason || dataToCopy.reason, equipment: currentAdjustmentData.equipment || dataToCopy.equipment });
            return data;
        }, {});
        setAdjustmentData(dataToApply);
    };
    const onShipmentDetailsModalHide = () => {
        setShipment('');
    };
    const disableCreateAdjustments = () => {
        const totaldata = Object.keys(adjustmentData).reduce((data, shipment) => {
            const adjustment = adjustmentData[shipment];
            if ((adjustment.shipper || adjustment.carrier) && !adjustment.reason) {
                data.push(true);
            }
            data.push(false);
            return data;
        }, []);
        return totaldata.some((disable) => disable);
    };
    const removeShipment = (reference) => (shipment) => {
        setShipmentData((currentShipmentData) => {
            const data = cloneDeep(currentShipmentData);
            const shipments = data[reference].shipments;
            const shipmentIndex = shipments.findIndex((s) => s._id === shipment);
            if (shipmentIndex > -1) {
                shipments.splice(shipmentIndex, 1);
            }
            if (!shipments.length) {
                delete data[reference];
                return data;
            }
            return Object.assign(Object.assign({}, data), { [reference]: {
                    shipments,
                } });
        });
        setAdjustmentData((currentAdjustmentData) => {
            const data = cloneDeep(currentAdjustmentData);
            delete data[shipment];
            return data;
        });
    };
    const getType = (ledger, shipment_type) => {
        if (isIntermodalShipmentType(shipment_type)) {
            return ledger === RECEIVABLES ? SHIPPER_COST : CARRIER_COST;
        }
        return INTERNAL_ADJUSTMENT;
    };
    const getCategory = (shipment_type) => {
        return isIntermodalShipmentType(shipment_type)
            ? BASE_COST
            : RATE_ADJUSTMENT;
    };
    const getMessage = (ledger, shipment_type, baseCostExists) => {
        if (!baseCostExists) {
            const message = ledger === RECEIVABLES ? 'Shipper Cost' : 'Carrier Cost';
            return message;
        }
        if (baseCostExists && !isIntermodalShipmentType(shipment_type)) {
            const message = ledger === RECEIVABLES
                ? 'Adjustment for Shipper Cost'
                : 'Adjustment for Carrier Cost';
            return message;
        }
        if (baseCostExists && isIntermodalShipmentType(shipment_type)) {
            const message = ledger === RECEIVABLES
                ? 'Shipper Cost Adjustment'
                : 'Carrier Cost Adjustment';
            return message;
        }
        return 'Adjustment for Base Cost';
    };
    const mapAdjustment = (ledger, shipment_type, value, equipment, reason, baseCostExists) => ({
        type: getType(ledger, shipment_type),
        category: getCategory(shipment_type),
        adjusted_item: BASE_COST,
        message: getMessage(ledger, shipment_type, baseCostExists),
        value,
        equipment,
        reason,
        shipment_type,
    });
    const mapAdjustmentsPayload = () => {
        return Object.keys(adjustmentData).reduce((data, shipment) => {
            const adjustment = adjustmentData[shipment];
            if (!data[shipment]) {
                data[shipment] = {};
            }
            if (parseFloat(adjustment.shipper || '0') >= 0) {
                data[shipment].receivable = mapAdjustment(RECEIVABLES, adjustment.shipment_type, parseFloat(adjustment.shipper), adjustment.equipment, adjustment.reason, adjustment.shipperBaseCostExists);
            }
            if (parseFloat(adjustment.carrier || '0') >= 0) {
                data[shipment].payable = mapAdjustment(PAYABLES, adjustment.shipment_type, parseFloat(adjustment.carrier), adjustment.equipment, adjustment.reason, adjustment.carrierBaseCostExists);
            }
            return data;
        }, {});
    };
    const createAdjustments = async () => {
        try {
            setLoading(true);
            const result = await ShipmentUtils.createBaseCostAdjustments(mapAdjustmentsPayload()).catch((e) => console.log(e));
            if (result.entity.success) {
                resetData();
                setLoading(false);
            }
        }
        catch (e) {
            setLoading(false);
            console.log(e);
        }
    };
    const reuploadData = () => {
        resetData();
        setModalVisibility(true);
    };
    return (React.createElement(React.Fragment, null,
        uploading && React.createElement(Overlay, { text: t('UPLOADING') + '...' }),
        React.createElement(Container, { className: "py-8" },
            React.createElement("h3", { className: "my-0 mb-4 font-medium" }, t('BULK_BASE_COST_ADJUSTMENTS')),
            React.createElement(InfoPanel, { shipmentData: shipmentData }),
            React.createElement("div", { className: "pb-4" },
                React.createElement("button", { className: "button button-green", onClick: reuploadData }, t('RE_UPLOAD'))),
            React.createElement("a", { onClick: () => FileDownload(csvTemplate, 'bulk-base-cost-adjustment.csv'), type: "button", title: t('DOWNLOAD_TEMPLATE'), className: "button button-green flex-grow-0 flex-shrink-0 inline-block mb-8", download: true },
                React.createElement("div", { className: "flex space-x-1 items-center" },
                    React.createElement("span", null, t('TEMPLATE')),
                    React.createElement(DownloadIcon, { className: "w-4 h-4" }))),
            React.createElement("div", { style: gridColumnStyle, className: "grid gap-2 h-12" },
                React.createElement("p", null),
                React.createElement("h4", { className: "m-0 pl-2 pt-2", style: {
                        background: '#a8f0db',
                    } }, titleize(t('SHIPPER', { count: 1 }))),
                React.createElement("h4", { className: "m-0 pl-2 pt-2", style: {
                        background: 'lightgray',
                    } }, titleize(t('CARRIER', { count: 1 }))),
                React.createElement("p", null)),
            React.createElement(UploadModal, { showModal: showModal, setShipmentData: setShipmentData, setAdjustmentData: setAdjustmentData, setModalVisibility: setModalVisibility, setUploading: setUploading, onHide: onHideModal }),
            currentShipment && (React.createElement(ShipmentDetailsModal, { show: !!currentShipment, shipmentId: currentShipment, onHide: onShipmentDetailsModalHide })),
            React.createElement(List, { shipmentData: shipmentData, adjustmentData: adjustmentData, setAdjustmentData: setAdjustmentData, applyToAll: applyToAll, setShipment: setShipment, removeShipment: removeShipment }),
            React.createElement("div", { className: "flex mt-4" },
                React.createElement("span", { className: "ml-auto" },
                    React.createElement("button", { disabled: isEmpty(shipmentData) ||
                            isEmpty(adjustmentData) ||
                            disableCreateAdjustments(), className: "button button-green", onClick: createAdjustments },
                        !loading && t('CREATE_ADJUSTMENTS'),
                        loading && React.createElement(LoadingCircle, { text: t('CREATING') + '...' })))))));
};
export default BulkAdjustments;
